import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { ConfigNeeded } from "@/components/ConfigNeeded";
import { Mail, Loader2, CheckCircle2, XCircle, MailOpen, MousePointerClick, AlertTriangle, User } from "lucide-react";
import { Link } from "react-router-dom";

const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function MesEmails() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [ins, setIns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: s }, { data: i }] = await Promise.all([api.get("/emails"), api.get("/emails/stats"), api.get("/emails/insights").catch(() => ({ data: null }))]);
      setRows(r); setStats(s); setIns(i);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader title="Mes emails" subtitle="Historique réel de vos envois d'emails" icon={Mail} />

      {stats && !stats.resend_configured && (
        <div className="mb-4">
          <ConfigNeeded title="Envoi d'emails à configurer" testid="emails-config-needed">
            Ajoutez votre clé <span className="font-mono">RESEND_API_KEY</span> dans l'onglet Secrets (puis un domaine vérifié + <span className="font-mono">RESEND_FROM</span>) pour envoyer des emails à vos clients. L'historique ci-dessous reste réel : aucun envoi n'est simulé.
          </ConfigNeeded>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="emails-stats">
          <Stat label="Total envois" value={stats.total} />
          <Stat label="Réussis" value={stats.sent} tone="emerald" />
          <Stat label="Échecs" value={stats.failed} tone="red" />
          <Stat label="Dernier envoi" value={stats.last_at ? fmt(stats.last_at) : "—"} small />
        </div>
      )}

      {ins && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4" data-testid="emails-insights">
          <Stat label="Délivrés" value={ins.delivered} tone="emerald" />
          <Stat label="Ouverts" value={ins.opened} />
          <Stat label="Cliqués" value={ins.clicked} />
          <Stat label="Rejetés (bounce)" value={ins.bounced} tone="red" />
          <Stat label="Plaintes spam" value={ins.complaint} tone="red" />
        </div>
      )}

      <div className="flex gap-2 mb-4 flex-wrap" data-testid="emails-filters">
        {[["all", "Tous"], ["delivered", "Délivrés"], ["opened", "Ouverts"], ["action", "Action requise"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`email-filter-${k}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filter === k ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div> :
        (() => {
          const shown = rows.filter((e) => filter === "all" ? true : filter === "delivered" ? e.delivered : filter === "opened" ? e.opened : (e.bounced || e.status === "echec"));
          return shown.length === 0 ? <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500" data-testid="emails-empty">Aucun email pour ce filtre.</div> : (
        <div className="space-y-2" data-testid="emails-list">
          {shown.map((e) => {
            const action = e.bounced || e.status === "echec";
            return (
            <div key={e.id} data-testid={`email-${e.id}`} className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl p-4">
              {action ? <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                : e.status === "envoye" ? <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                : <XCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900 truncate">{e.subject || "(sans objet)"}</div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">À {e.to}{e.invoice_number ? ` · Facture ${e.invoice_number}` : ""}</div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {e.delivered && <Pill tone="emerald" icon={CheckCircle2}>Délivré</Pill>}
                  {e.opened && <Pill icon={MailOpen}>Ouvert{e.open_count > 1 ? ` ×${e.open_count}` : ""}</Pill>}
                  {e.clicked && <Pill icon={MousePointerClick}>Cliqué{e.click_count > 1 ? ` ×${e.click_count}` : ""}</Pill>}
                  {e.bounced && <Pill tone="red" icon={AlertTriangle}>Non délivré</Pill>}
                  {e.complaint && <Pill tone="red">Spam</Pill>}
                </div>
                {action && (
                  <div className="text-xs text-red-500 mt-1.5 flex items-center gap-2">
                    <span>🔴 Vérifiez l'adresse email du client.</span>
                    {e.client_id && <Link to={`/clients/${e.client_id}`} data-testid={`email-open-client-${e.id}`} className="inline-flex items-center gap-1 text-emerald-600 hover:underline"><User className="w-3 h-3" /> Ouvrir le client</Link>}
                  </div>
                )}
                {e.status === "echec" && e.error && <div className="text-xs text-red-500 mt-1 line-clamp-2">{e.error}</div>}
              </div>
              <div className="text-[11px] text-slate-400 shrink-0">{fmt(e.created_at)}</div>
            </div>
          );})}
        </div>
      );})()}
    </div>
  );
}

function Pill({ children, tone, icon: Icon }) {
  const c = tone === "emerald" ? "bg-emerald-50 text-emerald-700" : tone === "red" ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-600";
  return <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${c}`}>{Icon && <Icon className="w-3 h-3" />}{children}</span>;
}

function Stat({ label, value, tone, small }) {
  const c = tone === "emerald" ? "text-emerald-600" : tone === "red" ? "text-red-600" : "text-slate-900";
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`font-heading font-bold mt-1 ${small ? "text-sm" : "text-2xl"} ${c}`}>{value}</div>
    </div>
  );
}
